import { Controller } from "@hotwired/stimulus"

/**
 * Asks the server what the listing's website returns, and lets the answer be acted on.
 *
 * The fetch is not done here for two reasons, and either one on its own would be enough.
 * CORS: a cross-origin response reaches JavaScript opaque, with no status code in it, so
 * the number this whole feature is about only exists on the server that made the request.
 * And SSRF: a url off a form is an arbitrary target, so the request has to be made
 * somewhere that can refuse to point it at an internal address - see LinkCheck#permitted?.
 *
 * So this points a turbo frame at a URL and stops. The badge, its colour and every word in
 * it are rendered from ERB, which is why i18n-tasks is configured to ignore JS paths.
 *
 * On connect rather than on a button, because the answer is worth having before anybody
 * asks: an outdated link is exactly the thing nobody thinks to check.
 *
 * #useSuggested is the one thing here that touches the form. When the site answers at a
 * different address than the one on file, the badge offers that address, and taking the
 * offer fills the two fields rather than saving: a redirect can be locale or geography
 * dependent - a European storefront bouncing our US datacentre to its US site - so the
 * last word belongs to the person reading it, not to whatever answered our request.
 *
 * #checkAgain is the badge's own button. The connect-time check runs on the page load's
 * short deadline, and a slow storefront fails it as "No response", so the button points the
 * frame at the same check with the batch's patience. It shows the frame's "Checking…" line
 * first - rendered hidden by the partial beside the answer - because the answer can take ten
 * seconds, and a badge that sits unchanged reads as a button that did nothing. The controller
 * only toggles the two; their markup and words are ERB's.
 *
 * It asks about the address in the box, not the one on file. The connect-time check is
 * about what is saved, but the button is pressed by somebody who has just typed a fix or
 * taken the redirect badge's offer, and the question they are asking is whether that
 * answers - before they save it, not after. The two fields go along as they are; the
 * server composes them by the model's own rule and, for an address that is not the saved
 * one, records nothing. See OnlineDealers::FormLinkCheck.
 *
 * #syncOpenLink is the other thing here that touches the form, and it touches only the
 * link beside it: the "open the site" button has to point at the address in the box, not
 * the one on file, or it would send somebody to the old page in the moment they are
 * fixing it - and it would have nowhere to send them at all on a listing being created.
 *
 * Expected markup:
 *   <div data-controller="link-check"
 *        data-link-check-url-value="/…/link-check">
 *     <select data-link-check-target="scheme" data-action="change->link-check#syncOpenLink">…</select>
 *     <input  data-link-check-target="address" data-action="input->link-check#syncOpenLink">
 *     <a      data-link-check-target="openLink" href="…" target="_blank">…</a>
 *     <turbo-frame id="listing-link-check" data-link-check-target="frame"></turbo-frame>
 *   </div>
 */
export default class extends Controller {
  static targets = ["frame", "scheme", "address", "openLink", "result", "pending"]
  static values = { url: String }

  connect() {
    // Before the frame guard below: the open link is on the new form too, where there is
    // no listing to ask about and so nothing else in this controller has any work to do.
    this.syncOpenLink()

    if (!this.hasFrameTarget || !this.urlValue) return

    this.#load(this.urlValue)
  }

  /**
   * Ask about the address in the box, on the patient url the button carries, showing the
   * frame's pending line in place of its answer until the new one arrives.
   */
  checkAgain(event) {
    event.preventDefault()

    const { url } = event.params
    if (!this.hasFrameTarget || !url) return

    this.resultTargets.forEach((element) => { element.hidden = true })
    this.pendingTargets.forEach((element) => { element.hidden = false })

    this.#load(this.#withTypedAddress(url))
  }

  /**
   * The button's url with the two fields added as they stand. Sent raw rather than
   * composed here: the server puts them together by the model's own rule - a scheme
   * pasted into the address wins over the toggle, see OnlineDealerListing.compose_url -
   * so the address it checks is the one Save would store, and this file holds no second
   * copy of that rule to drift from it. Without an address field there is nothing to
   * send, and the check is about the saved address, as on load.
   *
   * @param {string} url
   * @returns {string} a path, so the frame's src stays relative like the one it was given
   */
  #withTypedAddress(url) {
    if (!this.hasAddressTarget) return url

    const target = new URL(url, window.location.origin)
    target.searchParams.set("address", this.addressTarget.value)
    if (this.hasSchemeTarget) target.searchParams.set("scheme", this.schemeTarget.value)
    return `${target.pathname}${target.search}`
  }

  /**
   * Point the frame at a url, or reload it if it is already there.
   *
   * Setting src to what it already is loads nothing. The form opts out of Turbo's page
   * cache, but a frame that has kept its src through a restore would sit on a stale answer
   * forever - and a second Check again would do nothing at all. Asking the frame to reload
   * is the same request, made again.
   *
   * @param {string} url
   */
  #load(url) {
    if (this.frameTarget.getAttribute("src") === url && typeof this.frameTarget.reload === "function") {
      this.frameTarget.reload()
    } else {
      this.frameTarget.src = url
    }
  }

  /**
   * Split the offered address across the scheme dropdown and the address field, which is
   * the shape the form edits a url in. The model would recompose a pasted scheme anyway -
   * see OnlineDealerListing#recompose_url - but leaving "https://" sitting in the address
   * box next to a dropdown that also says https reads as a mistake the tool just made.
   */
  useSuggested(event) {
    event.preventDefault()

    const suggested = event.params.address
    if (!suggested || !this.hasAddressTarget) return

    const match = suggested.match(/^(https?):\/\/(.*)$/i)
    if (match && this.hasSchemeTarget) this.schemeTarget.value = match[1].toLowerCase()
    this.addressTarget.value = match ? match[2] : suggested

    // The field has changed under the cursor rather than because of it, so say so: any
    // listener on the form hears it, and the focus is the shortest route to the Save the
    // admin still has to press.
    this.addressTarget.dispatchEvent(new Event("input", { bubbles: true }))
    this.addressTarget.focus()
  }

  /**
   * Point the open-the-site link at whatever the two fields currently say.
   *
   * The url is composed by the same rule the model saves by - a scheme pasted into the
   * address wins over the dropdown, see OnlineDealerListing#recompose_url - so the tab
   * that opens is the page the listing is about to hold. That rule is also what keeps
   * this safe: the address never becomes the href on its own, so a "javascript:" typed
   * into the box composes to https://javascript:… and navigates nowhere interesting.
   *
   * An empty address leaves the link flat rather than removing it. A control that
   * disappears while somebody types is a control they have to find again, and the button
   * they were reaching for is the one that moved.
   */
  syncOpenLink() {
    if (!this.hasOpenLinkTarget) return

    const typed = this.hasAddressTarget ? this.addressTarget.value.trim() : ""
    const match = typed.match(/^(https?):\/\/(.*)$/i)
    // hasSchemeTarget rather than optional chaining: a missing Stimulus target throws
    // from the getter, so there is nothing for ?. to reach.
    const fallbackScheme = this.hasSchemeTarget ? this.schemeTarget.value : "https"
    const scheme = match ? match[1].toLowerCase() : fallbackScheme
    const address = match ? match[2] : typed

    this.openLinkTarget.href = address ? `${scheme}://${address}` : "#"
    this.openLinkTarget.classList.toggle("disabled", !address)
    this.openLinkTarget.setAttribute("aria-disabled", address ? "false" : "true")
    this.openLinkTarget.tabIndex = address ? 0 : -1
  }
}
